import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import type { PermissionKey } from '../domain/auth'
import { authenticatedHomePath } from '@/config/env'
import { hasAnyPermission, hasPermission, homePathFor, passwordChangePath } from '../domain/authorization'
import { useAuth } from './useAuth'

export function AuthLoadingState() {
  return (
    <main className="auth-loading" aria-busy="true" aria-live="polite">
      <span className="auth-loading__mark" aria-hidden="true" />
      <p>Validation de votre accès sécurisé…</p>
    </main>
  )
}

export function RequireAuth() {
  const { session, loading } = useAuth()
  const location = useLocation()
  if (loading) return <AuthLoadingState />
  // La chaîne de requête fait partie de la destination : `/quotes/new?clientId=…`
  // doit être restituée intégralement après la connexion.
  if (!session)
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  // Mot de passe temporaire : aucun module avant son remplacement (le serveur n'accorde d'ailleurs aucun droit).
  if (session.status === 'PASSWORD_CHANGE_REQUIRED' && location.pathname !== passwordChangePath)
    return <Navigate to={passwordChangePath} replace />
  if (session.status !== 'PASSWORD_CHANGE_REQUIRED' && location.pathname === passwordChangePath)
    return <Navigate to={homePathFor(session, authenticatedHomePath)} replace />
  return <Outlet />
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: PermissionKey
  children?: ReactNode
}) {
  const { session, loading } = useAuth()
  if (loading) return <AuthLoadingState />
  if (!session) return <Navigate to="/login" replace />
  if (!hasPermission(session, permission)) return <Navigate to="/forbidden" replace />
  return children ?? <Outlet />
}

export function RequireAnyPermission({
  permissions,
  children,
}: {
  permissions: readonly PermissionKey[]
  children?: ReactNode
}) {
  const { session, loading } = useAuth()
  if (loading) return <AuthLoadingState />
  if (!session) return <Navigate to="/login" replace />
  if (!hasAnyPermission(session, permissions)) return <Navigate to="/forbidden" replace />
  return children ?? <Outlet />
}

export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: PermissionKey
  children: ReactNode
  /** Rendu à la place du contenu quand la permission manque (ex. explication d'un onglet inaccessible). */
  fallback?: ReactNode
}) {
  const { session } = useAuth()
  return session && hasPermission(session, permission) ? children : fallback
}
